import { css, StyleSheet } from 'aphrodite';
import { type FC, useMemo } from 'react';

import { Industry } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useAutomationTemplatesQuery } from '../../../queries/Automation';
import { useMyTemplatesQuery } from '../../../queries/Templates/useMyTemplatesQuery';
import { AutomationIcon } from '../../../web/components/svgs/icons/AutomationIcon';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../web/components-v2/Select';
import { BLANK_OPTION_VALUE, type ComposerSelection, selectionToValue } from './types';

export type TemplateAutomationSelectProps = {
	selection: ComposerSelection;
	onSelectionChange: (selection: ComposerSelection) => void;
	disabled?: boolean;
};

/**
 * Unified "template or automation" selector — a modern react-query replacement
 * for the legacy MobX `TemplateSelector`/`EmailCampaignBrowserViewModel`
 * (`aida/components/EmailEditor/index.tsx:153-177`). Merges email templates and
 * automation templates into one dropdown; automation rows carry an icon. The
 * caller (the dialog) maps the chosen `ComposerSelection` to the right mode:
 * templates load content + send, automations enroll the contact.
 */
export const TemplateAutomationSelect: FC<TemplateAutomationSelectProps> = ({
	selection,
	onSelectionChange,
	disabled = false,
}) => {
	const userSession = useUserSession();
	const industry = userSession?.account?.additionalInfo?.industry;
	// Mirror the legacy composer's template source exactly: `template/{industry}/me`
	// (all of the user's email templates for their industry, excluding expired,
	// sorted by last-modified) — not a paginated `byType` slice.
	const templatesQuery = useMyTemplatesQuery<{ id?: string; name?: string }>({
		industry: industry ?? Industry.Insurance,
		enabled: !!industry,
	});
	const automationsQuery = useAutomationTemplatesQuery({});

	const templates = useMemo(
		() => (templatesQuery.data ?? []).filter((t): t is { id: string; name?: string } => Boolean(t?.id)),
		[templatesQuery.data]
	);

	const automations = useMemo(
		() => (automationsQuery.data ?? []).filter((t): t is typeof t & { id: string; name: string } => Boolean(t?.id)),
		[automationsQuery.data]
	);

	// base-ui's SelectValue renders the raw value string ("template:<id>"), so we
	// compute and render the friendly label ourselves.
	const selectedLabel = useMemo(() => {
		if (selection.kind === 'blank') return 'Blank Template';
		if (selection.kind === 'template') {
			return selection.name || templates.find(t => t.id === selection.id)?.name || 'Template';
		}
		return selection.name || automations.find(a => a.id === selection.id)?.name || 'Automation';
	}, [selection, templates, automations]);

	const onValueChange = (value: string) => {
		if (value === BLANK_OPTION_VALUE) {
			onSelectionChange({ kind: 'blank' });
			return;
		}
		const [kind, id] = value.split(/:(.+)/);
		if (kind === 'template') {
			const t = templates.find(x => x.id === id);
			onSelectionChange({ kind: 'template', id, name: t?.name ?? '' });
		} else if (kind === 'automation') {
			const a = automations.find(x => x.id === id);
			onSelectionChange({ kind: 'automation', id, name: a?.name ?? '' });
		}
	};

	return (
		<Select value={selectionToValue(selection)} onValueChange={onValueChange} disabled={disabled}>
			<SelectTrigger>
				<span className={css(styles.triggerLabel)}>{selectedLabel}</span>
			</SelectTrigger>
			<SelectContent>
				<SelectItem value={BLANK_OPTION_VALUE}>Blank Template</SelectItem>
				{templates.map(t => (
					<SelectItem key={`template:${t.id}`} value={`template:${t.id}`}>
						{t.name || 'Untitled template'}
					</SelectItem>
				))}
				{automations.map(a => (
					<SelectItem key={`automation:${a.id}`} value={`automation:${a.id}`}>
						<span className={css(styles.automationRow)}>
							<AutomationIcon className={css(styles.automationIcon)} />
							{a.name || 'Untitled automation'}
						</span>
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
};

const styles = StyleSheet.create({
	triggerLabel: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	automationRow: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
	},
	automationIcon: {
		width: 14,
		height: 14,
	},
});
